// Desktop-optimized Home View
import React from 'react';
import { Play, Ghost, LogOut, Clock, Loader, BarChart2, ShieldCheck, TrendingUp, Trophy, Settings, RefreshCw, Target } from 'lucide-react';

const DesktopHomeView = ({ 
    currentUserData, 
    onStartNewMatch, 
    onLogout, 
    matchHistory, 
    loadingHistory, 
    invites,
    onViewStats, 
    onViewPractice,
    onViewDrill,
    onReviewMatch,
    isMatchInProgress,
    onResumeMatch,
    onNavigateToProfile,
    onAcceptChallenge,
    onDeclineChallenge,
    PlayerStatsSummaryCard,
    PendingInvites,
    formatDate
}) => {
    return (
        <div className="min-h-screen flex">
            {/* Sidebar Navigation */}
            <div className="w-64 bg-slate-800/50 border-r border-slate-700 min-h-screen">
                <div className="p-6">
                    <h1 className="text-xl font-bold text-white mb-6">Pool Tracker</h1>
                    
                    {/* Navigation Menu */}
                    <nav className="space-y-2">
                        <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                            <div className="flex items-center gap-3 text-green-400 font-semibold">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span>Dashboard</span>
                            </div>
                        </div>
                        
                        <button 
                            onClick={onViewStats}
                            className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors text-slate-300 hover:text-white"
                        >
                            <BarChart2 className="w-5 h-5" />
                            <span>Performance Stats</span>
                        </button>
                        
                        <button 
                            onClick={onViewPractice}
                            className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors text-slate-300 hover:text-white"
                        >
                            <Ghost className="w-5 h-5" />
                            <span>Practice Stats</span>
                        </button>
                        
                        <button 
                            onClick={onViewDrill}
                            className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors text-slate-300 hover:text-white"
                        >
                            <Target className="w-5 h-5" />
                            <span>Drill Tracker</span>
                        </button>
                    </nav>

                    {/* User Actions */}
                    <div className="mt-8 pt-6 border-t border-slate-700">
                        <div className="space-y-2">
                            <button 
                                onClick={onNavigateToProfile}
                                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors text-slate-300 hover:text-white"
                            >
                                <Settings className="w-5 h-5" />
                                <span>Settings</span>
                            </button>
                            <button 
                                onClick={onLogout}
                                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors text-slate-300 hover:text-white"
                            >
                                <LogOut className="w-5 h-5" />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-white mb-2">
                            Welcome back, {currentUserData?.name}!
                        </h1>
                        <p className="text-slate-400">Ready to track your next pool game?</p>
                    </div>

                    {/* Pending Invites */}
                    <PendingInvites 
                        invites={invites}
                        currentUserData={currentUserData}
                        onAcceptChallenge={onAcceptChallenge}
                        onDeclineChallenge={onDeclineChallenge}
                    />

                    {/* Main Action Button */}
                    <div className="mb-8">
                        {isMatchInProgress ? (
                            <button 
                                onClick={onResumeMatch} 
                                className="bg-yellow-500 text-black px-8 py-4 rounded-xl font-semibold text-lg hover:bg-yellow-600 transition-all flex items-center gap-3 shadow-lg"
                            >
                                <RefreshCw className="w-6 h-6" /> Resume Match in Progress
                            </button>
                        ) : (
                            <button 
                                onClick={onStartNewMatch} 
                                className="bg-green-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-green-700 transition-all flex items-center gap-3 shadow-lg"
                            >
                                <Play className="w-6 h-6" /> Start New Match
                            </button>
                        )}
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {/* Left Column - Dashboard Stats */}
                        <div className="xl:col-span-1">
                            <PlayerStatsSummaryCard currentUserData={currentUserData} matchHistory={matchHistory} />
                        </div>

                        {/* Right Column - Match History */}
                        <div className="xl:col-span-2">
                            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 h-fit">
                                <h2 className="text-xl font-bold text-white mb-6">Recent Match History</h2>
                                {loadingHistory ? (
                                    <div className="flex justify-center items-center p-8">
                                        <Loader className="animate-spin text-slate-400" />
                                        <span className="ml-2 text-slate-400">Loading history...</span>
                                    </div>
                                ) : matchHistory && matchHistory.length > 0 ? (
                                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                        {matchHistory.slice(0, 10).map(match => {
                                            const isPlayer1 = match.matchData.player1Id === currentUserData.uid;
                                            const opponentName = isPlayer1 ? match.matchData.player2 : match.matchData.player1;
                                            const opponentIsProfile = isPlayer1 ? match.matchData.player2IsProfile : match.matchData.player1IsProfile;
                                            
                                            const myScore = isPlayer1 ? match.gameState.player1Score : match.gameState.player2Score;
                                            const opponentScore = isPlayer1 ? match.gameState.player2Score : match.gameState.player1Score;
                                            
                                            const isWin = match.gameState.winner === (isPlayer1 ? 'player1' : 'player2');
                                            const isSimpleTracked = match.matchData.trackingMode === 'simple';
                                            const isForfeited = match.gameState.forfeited;

                                            return (
                                            <button 
                                                key={match.id} 
                                                className={`w-full text-left bg-slate-700/50 p-4 rounded-lg transition-colors border border-slate-600/50 hover:border-slate-500 ${
                                                    isSimpleTracked 
                                                        ? 'cursor-not-allowed opacity-70' 
                                                        : 'hover:bg-slate-700/70'
                                                }`}
                                                onClick={() => onReviewMatch(match)}
                                                disabled={isSimpleTracked}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div className="flex flex-col">
                                                        <div className="font-bold text-white flex items-center gap-2 text-lg">
                                                            {match.matchData.gameType}
                                                            {isSimpleTracked && <span className="text-xs font-normal bg-slate-600 px-2 py-1 rounded-full text-slate-300">Score Only</span>}
                                                        </div>
                                                        <div className="text-sm text-slate-400 flex items-center gap-2 mt-1">
                                                            <span>vs. {opponentName}</span>
                                                            {opponentIsProfile && <ShieldCheck size={14} className="text-green-400" title="Registered Player" />}
                                                        </div>
                                                    </div>

                                                    <div className="font-semibold text-2xl text-white">
                                                        <span>{myScore}</span>
                                                        <span className="mx-2 text-slate-500">-</span>
                                                        <span>{opponentScore}</span>
                                                    </div>

                                                    <div className="flex flex-col items-end">
                                                         <div className={`font-bold text-xl ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                                                            {isWin ? 'WIN' : 'LOSS'}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                                            {isForfeited && <span className="font-semibold text-yellow-500">Forfeit</span>}
                                                            <Clock size={14}/>
                                                            <span>{formatDate(match.completedAt)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                            )
                                        })}
                                        {matchHistory.length > 10 && (
                                            <div className="text-center py-4">
                                                <button 
                                                    onClick={onViewStats}
                                                    className="text-blue-400 hover:text-blue-300 font-medium"
                                                >
                                                    View all {matchHistory.length} matches →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <div className="text-6xl mb-4">🎱</div>
                                        <h3 className="text-xl font-semibold text-white mb-2">No matches yet</h3>
                                        <p className="text-slate-400 mb-6">Start your first game to see your match history here!</p>
                                        <button 
                                            onClick={onStartNewMatch}
                                            className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                                        >
                                            Start Your First Match
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DesktopHomeView;
